import * as Yup from "yup";
import { useState, useContext } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import VisibilityIcon from "@mui/icons-material/Visibility";
// material
import {
  Stack,
  TextField,
  IconButton,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Link,
  Typography,
  Tooltip
} from "@mui/material";
import { Link as RouterLink } from "react-router-dom";
import LoadingButton from "@mui/lab/LoadingButton";
import isEmpty from "lodash/isEmpty";
import axios from "../../../util/axios";
import token from "../../../util/Token";
import { Context } from "../../../context";
import Constant from "../../../util/constants";
import { useDomainConfig } from "../../../hooks/useDomainConfig";
import OTPVerification from "../register/OTPVerification";

// ----------------------------------------------------------------------

const { paths, action } = Constant;

export default function RegisterForm({ isPopup, termsUrl, privacyUrl }) {
  const { auth } = useContext(Context);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState();
  const [openOtpVerify, setOpenOtpVerify] = useState(false);
  const [resendOtpPayloadObj, setResendOtpPayloadObj] = useState(null);
  const [contactType, setContactType] = useState("");
  
  const domainConfig = useDomainConfig();
  const phoneNoRegex = /^\d{10}$/i;
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i;

  const loginSchema = Yup.object().shape({
    // email: Yup.string().email('Email must be a valid email address').required('Email is required'),
    contact: Yup.string()
      .test(
        "phone-or-email",
        "Please enter a valid phone number or email address",
        (value) => {
          if (phoneNoRegex.test(value) || emailRegex.test(value)) return true;
          return false;
        }
      )
      .required("Either email or phone no. is required"),
    password: Yup.string().required("Password is required"),
  });

  const formik = useFormik({
    initialValues: {
      contact: "",
      password: "",
    },
    validationSchema: loginSchema,
    onSubmit: (loginData, { setSubmitting, resetForm }) => {
      setError("");
      (async function () {
        try {
          let loginPayload = {
            type: emailRegex.test(loginData.contact) ? "email" : "phone",
            password: loginData.password,
            domain: domainConfig.domain,
          };
          if (loginPayload.type === "email")
            loginPayload.email = loginData.contact;
          else {
            loginPayload.phone = loginData.contact;
            loginPayload.countryCode = "91";
          }
          const loginHelper = async () => {
            const response = await axios(
              `${process.env.REACT_APP_API_END_POINT}auth/login`,
              {
                method: "post",
                data: loginPayload,
              }
            );
            const tokenResp = response?.data?.data?.data;
            if (tokenResp && tokenResp.token) {
              const { authenticated, setAuthenticated } = auth;
              token.set(tokenResp.token);
              //  TODO: make it work
              //  Token.setAuthToken();
              //  window.location.href = '/dashboard';
              //  navigate('/dashboard');
              if (isPopup) {
                setAuthenticated({
                  ...authenticated,
                  authPopup: !1,
                  authElementType: action.AUTH_ELELEMT_TYPE_POPUP,
                });
                return;
              }
              setSubmitting(false);
              window.location.href = isEmpty(authenticated.redirectTo)
                ? paths.HOME
                : authenticated.redirectTo;
            } else {
              setError(response?.data?.message || "Login failed. Please check your credentials.");
              setSubmitting(false);
            }
          };
          await loginHelper();
        } catch (error) {
          setSubmitting(false);
          if (
            error.response &&
            error.response.data &&
            error.response.data.message
          ) {
            const errorMsg = error.response.data.message;
            if (errorMsg === "Email is not verified." || errorMsg === "Phone is not verified.") {
              const detectedType = emailRegex.test(loginData.contact) ? "email" : "phone";
              setContactType(detectedType);
              
              const otpPayload = {
                type: detectedType,
                domain: domainConfig.domain
              };
              if (detectedType === "email") {
                otpPayload.email = loginData.contact;
              } else {
                otpPayload.phone = loginData.contact;
                otpPayload.countryCode = "91";
              }
              setResendOtpPayloadObj(otpPayload);
              resendOtpFunc(otpPayload).then(() => {
                setOpenOtpVerify(true);
              }).catch(err => {
                setError(err || "Failed to send verification code.");
              });
            } else {
              setError(errorMsg);
            }
          }
        }
      })();
      //navigate('/dashboard', { replace: true });
    },
  });

  const resendOtpFunc = async (payload) => {
    return new Promise(async (res, rej) => {
      try {
        const response = await axios(
          `${process.env.REACT_APP_API_END_POINT}auth/v2/register-resend-otp`,
          {
            method: "post",
            data: payload || resendOtpPayloadObj,
          }
        );

        const registerHelperResp = response.data;
        if (registerHelperResp && registerHelperResp.success) {
          res("resend success");
        }
      } catch (error) {
        rej(error?.response?.data?.message || "Error resending OTP");
      }
    });
  };

  const submitOtp = (otp) => {
    return new Promise(async (res, rej) => {
      try {
        let otpPayload;
        if (contactType === "email") {
          otpPayload = {
            email: formik.values.contact,
            domain: domainConfig.domain,
            phoneOtp: otp.Otp,
            type: "email",
          };
        } else if (contactType === "phone") {
          otpPayload = {
            phone: formik.values.contact,
            countryCode: "91",
            domain: domainConfig.domain,
            phoneOtp: otp.Otp,
            type: "phone",
          };
        }

        const url = `${process.env.REACT_APP_API_END_POINT}auth/v2/register-verify-otp`;
        const response = await axios(url, {
          method: "post",
          data: otpPayload,
        });

        res(response.data);
      } catch (error) {
        rej(error.response.data.message);
      }
    });
  };

  const onSuccessfullVerify = (otp) => {
    setError("");
    setSuccessMessage("Account verified successfully! You can now log in.");
  };

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    resetForm,
  } = formik;

  return (
    <FormikProvider value={formik}>
      {error && <div style={{ color: "red", marginBottom: "16px", marginTop: 0, fontSize: "15px" }}>{error}</div>}
      {successMessage && <div style={{ color: "green", marginBottom: "16px" }}>{successMessage}</div>}
      <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
        <Stack spacing={3}>
          <TextField
            fullWidth
            autoComplete="username"
            type="text"
            label="Email address / Phone number"
            {...getFieldProps("contact")}
            inputProps={{ maxLength: 100 }}
            error={Boolean(touched.contact && errors.contact)}
            helperText={touched.contact && errors.contact}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem',
                fontWeight: 500,
              },
            }}
          />

          <TextField
            fullWidth
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            label="Password"
            {...getFieldProps("password")}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <Tooltip title={showPassword ? "Hide password" : "Show password"} placement="top">
                    <IconButton
                      edge="end"
                      onClick={() => setShowPassword((prev) => !prev)}
                      sx={{ 
                        color: 'text.secondary',
                        '&:hover': { color: 'primary.main' }
                      }}
                    >
                      {showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                  </Tooltip>
                </InputAdornment>
              ),
            }}
            error={Boolean(touched.password && errors.password)}
            helperText={touched.password && errors.password}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
                fontSize: '1rem',
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'primary.main',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderWidth: 2,
                },
              },
              '& .MuiInputLabel-root': {
                fontSize: '0.95rem',
                fontWeight: 500,
              },
            }}
          />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ my: 2 }}
          >
            <Link
              component={RouterLink}
              variant="subtitle2"
              to="/forget-password"
              sx={{
                textDecoration: 'none',
                color: 'primary.main',
                fontWeight: 600,
                '&:hover': {
                  textDecoration: 'underline',
                  color: 'primary.dark',
                },
              }}
            >
              Forgot password?
            </Link>
          </Stack>

          {(termsUrl || privacyUrl) && (
            <Typography variant="body2" align="center" sx={{ color: 'text.secondary', mt: 1, mb: 0, fontSize: '0.75rem', lineHeight: 1.5 }}>
              By logging in, I agree to <Link component={RouterLink} to="/terms-of-services" underline="always" sx={{ color: 'primary.main' }}>Terms of Service</Link> and <Link component={RouterLink} to="/privacy-policy" underline="always" sx={{ color: 'primary.main' }}>Privacy Policy</Link>.
            </Typography>
          )}

          <LoadingButton
            fullWidth
            size="large"
            type="submit"
            variant="contained"
            loading={isSubmitting}
            sx={{
              height: 52,
              fontSize: '1.1rem',
              fontWeight: 700,
              letterSpacing: '0.5px',
              textTransform: 'none',
              borderRadius: 3,
              background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
              boxShadow: '0 4px 14px 0 rgba(25, 118, 210, 0.39)',
              mt: 2,
              mb: 2,
              '&:hover': {
                background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)',
                boxShadow: '0 6px 20px 0 rgba(25, 118, 210, 0.5)',
                transform: 'translateY(-2px)',
              },
              '&:active': {
                transform: 'translateY(0)',
              },
              '&.Mui-disabled': {
                background: 'linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 100%)',
                color: 'text.disabled',
              },
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
          >
            {isSubmitting ? 'Logging In...' : 'Login'}
          </LoadingButton>
        </Stack>
      </Form>
      {openOtpVerify && (
        <OTPVerification
          title="Account Verification Required"
          subtitle={`Please verify your ${contactType} to continue logging in. We've sent a 4-digit code to ${formik.values.contact}.`}
          contactType={contactType}
          contact={formik.values.contact}
          onClose={() => setOpenOtpVerify(false)}
          open={openOtpVerify}
          submitOtpFunc={submitOtp}
          handleSuccess={onSuccessfullVerify}
          countryCode={contactType === "phone" ? "91" : undefined}
          resendOtp={resendOtpFunc}
        />
      )}
    </FormikProvider>
  );
}
